import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getClassDashboard, getEnterpriseDashboard, downloadEnterpriseReport, downloadClassReport,
  readableApiError, formatClassSubtitle, formatEnterpriseSubtitle,
} from '../../api/dashboard';
import { useAuth } from '../../context/AuthContext';
import { canAccessTrainingReco } from '../../auth/roles';
import { scoreBucketTone } from './_styles';
import './dashboard.css';

export default function ClassOverviewPage({ scope = 'class' }) {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [dlError, setDlError] = useState(null);
  const [clsDownloading, setClsDownloading] = useState(false);
  const [clsDlError, setClsDlError] = useState(null);

  const isEnt = scope === 'enterprise';
  const basePath = isEnt ? `/dashboard/enterprises/${id}` : `/dashboard/classes/${id}`;
  const backLabel = isEnt ? '← 返回企业列表' : '← 返回班级列表';

  useEffect(() => {
    setData(null); setError(null);
    (isEnt ? getEnterpriseDashboard(id) : getClassDashboard(id))
      .then(setData)
      .catch((err) => setError(readableApiError(err)));
  }, [id, isEnt]);

  if (error) return <div className="aiq-dash dash-page"><div className="dash-card dash-card--error">{error}</div></div>;
  if (!data) return <div className="aiq-dash dash-page"><div className="dash-card">加载中...</div></div>;

  const meta = isEnt ? data.enterprise : data.class;
  const subtitle = isEnt ? formatEnterpriseSubtitle(meta) : formatClassSubtitle(meta);

  // 返回链接：企业页回企业选择；班级页回「本企业」班级列表（带 ?enterprise= 过滤 + 企业名），
  // 避免 admin/超管 返回时看到跨企业全部班级。
  const backEntId = meta?.enterprise?.id;
  const backTo = isEnt
    ? '/dashboard/enterprises'
    : `/dashboard/classes${backEntId != null ? `?enterprise=${backEntId}` : ''}`;
  const backState = (!isEnt && meta?.enterprise?.name)
    ? { enterpriseName: meta.enterprise.name }
    : undefined;

  // 企业报告下载入口（spec §4.3）：仅企业全员页（R3）展示；班级页用「下载班级报告」即可，不再叠企业报告。
  // 仅企管员 / admin / 超管可见（与培训建议同角色集）。
  const reportEntId = isEnt ? id : (meta?.enterprise_id ?? meta?.enterprise ?? user?.enterprise_id);
  const showReportDownload = isEnt && canAccessTrainingReco(user) && reportEntId != null;
  const onDownloadReport = async () => {
    setDownloading(true); setDlError(null);
    const res = await downloadEnterpriseReport(reportEntId);
    setDownloading(false);
    if (!res.ok) setDlError(res.error || '下载失败');
  };

  // 班级报告下载入口（spec §4.3）：仅班级页 + enterprise_admin+（含培训建议）。
  const showClassReportDownload = !isEnt && canAccessTrainingReco(user);
  const onDownloadClassReport = async () => {
    setClsDownloading(true); setClsDlError(null);
    const res = await downloadClassReport(id);
    setClsDownloading(false);
    if (!res.ok) setClsDlError(res.error || '下载失败');
  };
  const { completion, team_avg_score, dimension_avgs, score_distribution } = data;
  const buckets = score_distribution || [];
  const maxBucketCount = Math.max(...buckets.map((x) => x.count), 1);
  const donePct = completion.total ? Math.round((completion.completed / completion.total) * 100) : 0;

  // 培训建议 tab 仅 enterprise_admin / admin / superadmin 可见（设计文档 4.3.3）
  const tabs = [
    ['', '总览'],
    ['/heatmap', '维度强弱'],
    ['/risks', '风险预警'],
    ['/distribution', '差异分布'],
    ...(canAccessTrainingReco(user) ? [['/recommendation', '培训建议']] : []),
  ];

  return (
    <div className="aiq-dash dash-page">
      <Link to={backTo} state={backState} className="dash-back">{backLabel}</Link>
      <div className="dash-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <h1 className="dash-h1">{meta.name}</h1>
          <p className="dash-sub">{subtitle}</p>
        </div>
        {(showReportDownload || showClassReportDownload) && (
          <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            {showClassReportDownload && (
              <div>
                <button type="button" onClick={onDownloadClassReport} disabled={clsDownloading}
                  style={{ background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8,
                    padding: '9px 18px', fontSize: 14, fontWeight: 600, cursor: clsDownloading ? 'default' : 'pointer',
                    opacity: clsDownloading ? 0.6 : 1 }}>
                  {clsDownloading ? '生成中…' : '下载班级报告'}
                </button>
                {clsDlError && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{clsDlError}</div>}
              </div>
            )}
            {showReportDownload && (
              <div>
                <button type="button" onClick={onDownloadReport} disabled={downloading}
                  style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8,
                    padding: '9px 18px', fontSize: 14, fontWeight: 600, cursor: downloading ? 'default' : 'pointer',
                    opacity: downloading ? 0.6 : 1 }}>
                  {downloading ? '生成中…' : '下载企业报告'}
                </button>
                {dlError && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{dlError}</div>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 子页导航 */}
      <div className="dash-toolbar">
        <div className="dash-tabs">
          {tabs.map(([suffix, label]) => (
            <Link key={label} to={`${basePath}${suffix}`}
              className={`dash-tab${suffix === '' ? ' is-active' : ''}`}>
              {label}
            </Link>
          ))}
        </div>
      </div>

      {/* KPI 卡 — 团队均分 hero, 其余完成度拆开避免"未完成/中"歧义 */}
      <div className="dash-kpi">
        <div className="dash-tile dash-tile--hero">
          <p className="dash-tile__k">团队均分</p>
          <p className="dash-tile__v">{team_avg_score ?? '—'}</p>
          <p className="dash-tile__cap">满分 100 · {completion.total} 人参与</p>
        </div>
        <div className="dash-tile">
          <p className="dash-tile__k">总人数</p>
          <p className="dash-tile__v">{completion.total}</p>
          <span className="dash-st dash-st--teal"><span className="dash-dot" />全部成员</span>
        </div>
        <div className="dash-tile">
          <p className="dash-tile__k">已完成</p>
          <p className="dash-tile__v">{completion.completed}</p>
          <span className="dash-st dash-st--good"><span className="dash-dot" />{donePct}%</span>
        </div>
        <div className="dash-tile">
          <p className="dash-tile__k">进行中</p>
          <p className="dash-tile__v">{completion.in_progress}</p>
          <span className="dash-st dash-st--warn"><span className="dash-dot" />作答中</span>
        </div>
        <div className="dash-tile">
          <p className="dash-tile__k">未开始</p>
          <p className="dash-tile__v">{completion.not_started}</p>
          <span className="dash-st dash-st--muted"><span className="dash-dot" />待邀请</span>
        </div>
      </div>

      {/* 维度团队均分 — dim_config_hardening:维度数会变,标题不写死 8;
          没有同行业基准数据,移除 industry_baseline 红线和脚注 */}
      <div className="dash-card">
        <div className="dash-sec"><h2>维度团队均分</h2><span className="dash-meta">满分 100</span></div>
        <div className="dash-dims">
          {dimension_avgs.map((d) => (
            <div key={d.code} className="dash-dim">
              <div className="dash-dim__nm">{d.name}</div>
              <div className="dash-dim__tk">
                {d.avg_score != null && (
                  <div className="dash-dim__fl" style={{ width: `${d.avg_score}%` }} />
                )}
              </div>
              <div className="dash-dim__vl">{d.avg_score ?? '—'}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 总分分布 */}
      <div className="dash-card">
        <div className="dash-sec"><h2>总分分布</h2><span className="dash-meta">最高档 {maxBucketCount} 人</span></div>
        <div className="dash-chart">
          {buckets.map((b) => {
            const label = b.range || b.bucket;
            const count = b.count || 0;
            const ratio = count / maxBucketCount;
            // 最高柱留 22px 给上方 count 数字 + gap, 避免越界遮 KPI 卡边界
            const barHeight = count ? Math.max(20, Math.round(ratio * 110)) : 3;
            const tone = scoreBucketTone(label);
            return (
              <div key={label} className="dash-bar-col" title={`${label}: ${count} 人`}>
                {/* bar absolute bottom:0 贴 axis, count 数字 absolute 浮 bar 顶上方, 与 bar 高度解耦。 */}
                <div className="dash-bar-wrap">
                  <div className="dash-bar" style={{
                    height: barHeight,
                    background: count ? tone.fill : '#eef2f8',
                    borderColor: count ? tone.stroke : '#cbd5e1',
                    borderWidth: 1,
                    borderStyle: 'solid',
                  }} />
                  <div className="dash-bar-n" style={{ bottom: barHeight + 6, color: count ? undefined : '#94a3b8' }}>
                    {count}
                  </div>
                </div>
                <div className="dash-bar-x">{label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
